import React, { FC, useState, useEffect, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { User as UserType } from '../../../types'
import { User as UserService } from '../../../services/User'
import { routes } from '../../../config'
import { useAppDispatch } from '../../hooks/store'
import { loadAutomats, loadOutlets } from '../../../store/slices/storage'
import { Layout } from '../../complexes/Layout'
import { Header } from '../../simples/tablePages/Header'
import { UserEditForm, ResultDataForm } from '../../complexes/UserEditForm'
import { UserAccessKeyEditor } from '../../complexes/UserAccessKeyEditor'

type RouteParams = {
    id: string
}

const EditCompanyUser: FC = () => {
    const { t } = useTranslation()

    const params = useParams<RouteParams>()
    const id = +params.id

    const history = useHistory()

    const [user, setUser] = useState<UserType>()
    const [saving, setSaving] = useState(false)

    const [showAccessKeyEditor, setShowAccessKeyEditor] = useState(false)

    const dispatch = useAppDispatch()
    useEffect(() => {
        dispatch(loadAutomats())
        dispatch(loadOutlets())
    }, [])

    useEffect(() => {
        UserService.getItem(id).then((user) => {
            setUser(user)
        })
    }, [id])

    const handleSave = async (
        data: ResultDataForm,
        deletePhoto: boolean = false
    ) => {
        setSaving(true)
        await UserService.updateById(id, {
            fullName: data.name,
            contacts: data.contacts,
            permissions: data.permissions,
        })

        if (data.photo) {
            await UserService.uploadPhotoByUserId(id, data.photo)
        } else if (deletePhoto) {
            await UserService.deletePhotoByUserId(id)
        }

        setSaving(false)

        history.push(routes.companyUsers)
    }

    const handleCancel = () => {
        history.push(routes.companyUsers)
    }

    const handleAccessKeyUserChange = (user: UserType) => {
        setUser(user)
    }

    const handleCreateAccessKeyClick = async () => {
        if (!user) {
            throw new Error('User is empty')
        }

        if (user.accessKey) {
            throw new Error('The user already has an access key')
        }

        const accessKey = await UserService.generateAccessKey(user.id)

        const updatedUser = {
            ...user,
            accessKey,
        }

        setUser(updatedUser)
        setShowAccessKeyEditor(true)
    }

    const userFormData = useMemo(() => {
        if (!user) {
            return {
                name: '',
                photo: '',
                contacts: [],
            }
        }

        return {
            name: user.fullName,
            photo: user.photoPath || '',
            contacts: user.contacts,
            permissions: user.permissions,
            accessKey: user.accessKey,
        }
    }, [user])

    return (
        <Layout>
            <Header text={t('359_EditCompanyUser_editUser')} />

            {user && (
                <>
                    <UserEditForm
                        data={userFormData}
                        showPermissionsFields
                        loading={saving}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        onOpenAccessKey={() => setShowAccessKeyEditor(true)}
                        onCreateAccessKey={handleCreateAccessKeyClick}
                    />

                    {showAccessKeyEditor && (
                        <UserAccessKeyEditor
                            user={user}
                            onUserChange={handleAccessKeyUserChange}
                            onClose={() => setShowAccessKeyEditor(false)}
                        />
                    )}
                </>
            )}
        </Layout>
    )
}

export default EditCompanyUser
